var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { PreviewConsentFlow, PreviewConsentMode, PreviewMetadata, PreviewParticipantType, PreviewRequestType, } from "healthex-common/preview.dtos";
import { StudyTranslationLanguage } from "healthex-common/study.dtos";
import { pick, toTitleCase, toTitleCaseWithSpaces, willNeverHappen } from "healthex-common/utils";
import { computed } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { FormModel } from "./FormModel";
import { languageDescription } from "./LanguageDescriptions";
const t = getObservableT("patients");
export class PatientConsentPreviewDialogModel {
    constructor(container) {
        this.isOpening = false;
        this.formModel = new FormModel({
            language: StudyTranslationLanguage.en,
            consentMode: PreviewConsentMode.REMOTE,
            consentFlow: PreviewConsentFlow.PATIENT_DIRECTED_DATA_EXCHANGE,
            participantType: PreviewParticipantType.ADULT,
            requestType: PreviewRequestType.INITIAL_HEALTH_RECORD_ACCESS,
        }, []);
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.studyPDFPreviewUIManager = container.studyPDFPreviewUIManager;
        this.bannerUIManager = container.bannerUIManager;
        this.formModel.onInputUpdate = async (input) => {
            if (!this.isOpening) {
                await this.restart();
                const whatChangedDescription = this.whatChangedDescription(input);
                if (whatChangedDescription) {
                    this.bannerUIManager.displayBanner({
                        title: `Switched to ${whatChangedDescription}`,
                        text: "You're now previewing from the beginning. Some steps or content may be different.",
                        type: "INFO",
                        durationInMS: 5000,
                    });
                }
            }
        };
    }
    onOpen() {
        if (this.patientConsentUIManager.session.interactivePreviewMetadata) {
            this.isOpening = true;
            try {
                this.formModel.updateInput(pick(this.patientConsentUIManager.session.interactivePreviewMetadata, "consentMode", "consentFlow", "language", "requestType"));
            }
            finally {
                this.isOpening = false;
            }
        }
    }
    async restart() {
        if (this.patientConsentUIManager.session.studyId && this.patientConsentUIManager.studyVersionId) {
            await this.patientConsentUIManager.startInteractivePreview(this.patientConsentUIManager.session.studyId, this.patientConsentUIManager.studyVersionId, new PreviewMetadata({
                consentMode: this.formModel.input.consentMode,
                consentFlow: this.formModel.input.consentFlow,
                language: this.formModel.input.language,
                participantType: this.formModel.input.participantType,
                requestType: this.formModel.input.requestType,
            }));
        }
    }
    get currentPreviewRoute() {
        return this.patientConsentUIManager.session.currentInteractivePreviewRoute;
    }
    get title() {
        if (!this.patientConsentUIManager.isInConsentFlow)
            return false;
        return "Enrollment informed consent experience";
    }
    get restartButtonDisabled() {
        if (!this.currentPreviewRoute)
            return false;
        return ["ENROLLMENT_WELCOME", "WELCOME"].includes(this.currentPreviewRoute);
    }
    get dialogTitle() {
        if (!this.patientConsentUIManager.isInConsentFlow)
            return null;
        return t("Patient enrollment flow");
    }
    get textInfo() {
        switch (this.currentPreviewRoute) {
            case "ENROLLMENT_WELCOME":
                return {
                    title: "Welcome to the Project",
                    explanation: "Introduces the patient to the request.",
                };
            case "ENROLLMENT_REQUEST_DETAILS_POPUP":
                return {
                    title: "Request Details",
                    explanation: "Full details of the consent request, including purpose, requested data, and recipients.",
                };
            case "ENROLLMENT_HOW_IT_WORKS":
                return {
                    title: "How HealthEx works",
                    explanation: "Outlines the steps to complete the request.",
                };
            case "ENROLLMENT_LOGIN":
                return {
                    title: "Identity Verification",
                    explanation: "The patient needs to verify their identity before proceeding.",
                };
            case "ENROLLMENT_CONFIRMED":
                return {
                    title: "Thank You Message - Consent Granted for Enrollment",
                    explanation: "Thanks the patient for their time reviewing the request. This is only shown to patients who say yes.",
                };
            case "ENROLLMENT_DECLINED":
                return {
                    title: "Thank You Message - No Consent Granted for Enrollment",
                    explanation: "Thanks the patient for their time reviewing the request. This is only shown to patients who say no.",
                };
            case "ENROLLMENT_EXCLUDED":
                return { title: "Ineligible", explanation: "Ineligible for project." };
            case "ENROLLMENT_CLEAR_UPGRADE":
                return {
                    title: "Find records automatically",
                    explanation: "Offers the patient the option to find their records automatically, which will require them to verify their identity with CLEAR.",
                };
            case "ENROLLMENT_PDDA_RECORDS_LOADING":
                return {
                    title: "Record Locator",
                    explanation: "Shows a list of the patient's places of care and the associated health records retrieved.",
                };
            case "ENROLLMENT_PDDA_RECORDS_NOT_FOUND":
                return {
                    title: "Records Not Found",
                    explanation: "Shows when no records are found for the patient's locations.",
                };
            case "ENROLLMENT_PDDA_RECORDS_FOUND_LIST":
                return {
                    title: "Records Found",
                    explanation: "Lists the locations where records were found, before the patient begins the login cycle.",
                };
            case "ENROLLMENT_PDDA_LOCATION_LOGIN":
                return {
                    title: "Location Login",
                    explanation: "Prompts the patient to authenticate with each location requiring login.",
                };
            case "ENROLLMENT_PDDA_DOCUMENT_SELECTION":
                return {
                    title: "Document Selection",
                    explanation: "Allows the patient to review and select which documents to share.",
                };
            case "ENROLLMENT_PDDA_ADD_PROVIDER":
                return {
                    title: "Add a Provider",
                    explanation: this.patientConsentUIManager.isHealthWalletEnabled
                        ? "Lets the patient add the providers whose records should be brought into their HealthEx Wallet."
                        : "Lets the patient add the providers whose records should be brought into their HealthEx Unified Record.",
                };
            case "ENROLLMENT_PDDA_REVIEW_AND_SHARE":
                return {
                    title: "Review and Share",
                    explanation: "Final review screen where the patient confirms sharing their retrieved records.",
                };
            case "ENROLLMENT_PDDA_CONTINUE_WITHOUT_SHARING":
                return {
                    title: "Continue Without Sharing",
                    explanation: "Shown when the patient chooses not to share any health records.",
                };
            case "ENROLLMENT_PDDA_HUR_LOADING":
                return {
                    title: this.patientConsentUIManager.isHealthWalletEnabled
                        ? "Preparing Your HealthEx Wallet"
                        : "Building Unified Record",
                    explanation: this.patientConsentUIManager.isHealthWalletEnabled
                        ? "Shows progress while the HealthEx Wallet is being prepared from the patient's shared documents."
                        : "Shows progress while the HealthEx Unified Record is being built from the patient's shared documents.",
                };
            case "ENROLLMENT_PDDA_SPECIFIC_LOCATION_CONFIRMED":
                return {
                    title: "Specific Location Login Successful",
                    explanation: "Shown after the patient successfully logs in to the specific provider from a direct record link.",
                };
            case "ENROLLMENT_PARTICIPATION_SIGNATURE":
                return {
                    title: "Participant Signature",
                    explanation: "Obtains the patient's signature to confirm their understanding and consent to grant access to their health records.\nNote: No consent will be logged in preview mode.",
                };
            case "ENROLLMENT_DECLINED_ACCESS":
                return {
                    title: "Declined Access",
                    explanation: "This screen informs the patient that they have declined to share their records.",
                };
            case "ENROLLMENT_USE_EXISTING_RECORDS":
                return {
                    title: "Use Existing Records",
                    explanation: "This screen informs the patient that they can use their existing records.",
                };
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN_PARTICIPANT_CONSENT_INTERIM_PAGE":
            case "ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_OUT_INTERIM_PAGE":
            case "ENROLLMENT_PARTICIPATION_SIGNATURE_SCREENSHOT_PREVIEW_DECLINED":
            case "ENROLLMENT_PDDA_RECORDS_LOADING_SCREENSHOT_REVIEW_AND_SHARE":
            case "ENROLLMENT_PDDA_RECORDS_LOADING_SCREENSHOT_RESULTS_FOUND_LIST":
            case "ENROLLMENT_PDDA_RECORDS_LOADING_SCREENSHOT_DOCUMENT_SELECTION":
            case "ENROLLMENT_EMAIL_COLLECTION":
            case "ENROLLMENT_SETUP_COMPLETION":
            case "ENROLLMENT_VERIFY_OTP":
            case null:
                return { title: "", explanation: "" };
        }
        return willNeverHappen(this.currentPreviewRoute);
    }
    get languagesEnabled() {
        return this.studyPDFPreviewUIManager.generatableLanguages;
    }
    get minorEnabled() {
        return this.patientConsentUIManager.isMinorConsentEnabled;
    }
    get requestTypeVisible() {
        return this.studyPDFPreviewUIManager.includeRequestTypes;
    }
    get consentModeVisible() {
        return this.studyPDFPreviewUIManager.includeGuided;
    }
    get projectPreviewOptions() {
        return this.studyPDFPreviewUIManager.projectPreviewOptions;
    }
    get participantTypeVisible() {
        return !!this.projectPreviewOptions.participantType;
    }
    whatChangedDescription(input) {
        if (input.language) {
            return `${languageDescription(input.language)} language`;
        }
        else if (input.consentMode) {
            return `${toTitleCase(input.consentMode)} consent mode`;
        }
        else if (input.consentFlow) {
            return `${toTitleCase(input.consentFlow)} consent flow`;
        }
        else if (input.participantType) {
            return `${toTitleCase(input.participantType)} participant type`;
        }
        else if (input.requestType) {
            return `${toTitleCaseWithSpaces(input.requestType)} request type`;
        }
        else {
            return null;
        }
    }
    get showLoginGradient() {
        return this.patientConsentUIManager.showClassicLoginPage;
    }
}
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "currentPreviewRoute", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "title", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "restartButtonDisabled", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "dialogTitle", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "textInfo", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "languagesEnabled", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "minorEnabled", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "requestTypeVisible", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "consentModeVisible", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "projectPreviewOptions", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "participantTypeVisible", null);
__decorate([
    computed
], PatientConsentPreviewDialogModel.prototype, "showLoginGradient", null);
